import type { ReactNode } from 'react';

/**
 * Основная кнопка экрана (ТЗ §14.3: «Одна основная CTA-кнопка на экран, внизу, во всю ширину»).
 *
 * Правило «одна на экран» соблюдается компоновкой, а не уговором: `CtaBar` — единственный
 * закреплённый снизу контейнер, и второй такой на том же экране наложился бы на первый
 * заметно.
 */
export function CtaBar(props: { children: ReactNode; withTabs?: boolean }): JSX.Element {
  return (
    <div
      className={`pointer-events-none absolute inset-x-0 ${
        props.withTabs ? 'bottom-[calc(env(safe-area-inset-bottom,0px)+58px)]' : 'bottom-0'
      } bg-gradient-to-b from-transparent to-bg to-[36%] px-4 pb-[calc(env(safe-area-inset-bottom,0px)+16px)] pt-3`}
    >
      <div className="pointer-events-auto">{props.children}</div>
    </div>
  );
}

export function CtaButton(props: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  variant?: 'primary' | 'danger' | 'ghost';
  className?: string;
}): JSX.Element {
  const variant = props.variant ?? 'primary';
  const base =
    'w-full rounded-[14px] px-4 py-[15px] text-[15px] font-bold transition disabled:cursor-not-allowed';
  const look =
    variant === 'primary'
      ? 'bg-accent text-on-accent disabled:bg-surface-2 disabled:text-ink-3'
      : variant === 'danger'
        ? 'bg-[color-mix(in_srgb,var(--red)_15%,transparent)] text-danger shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--red)_40%,transparent)]'
        : 'bg-surface-2 text-ink-2';

  return (
    <button
      type="button"
      onClick={props.onClick}
      disabled={props.disabled ?? false}
      className={`${base} ${look} ${props.className ?? ''}`}
    >
      {props.children}
    </button>
  );
}

/** Мелкая кнопка внутри карточки записи: «Повторить», «Отменить», «Подтвердить». */
export function SmallButton(props: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  variant?: 'accent' | 'ghost' | 'warn' | 'ok';
}): JSX.Element {
  const variant = props.variant ?? 'ghost';
  const look = {
    accent: 'bg-accent text-on-accent',
    ghost: 'bg-surface-2 text-ink-2',
    ok: 'bg-green text-[#04342c]',
    warn:
      'bg-[color-mix(in_srgb,var(--red)_16%,transparent)] text-danger shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--red)_40%,transparent)]',
  }[variant];

  return (
    <button
      type="button"
      onClick={props.onClick}
      disabled={props.disabled ?? false}
      className={`flex-1 rounded-[10px] px-2 py-2.5 text-[12px] font-bold transition disabled:opacity-50 ${look}`}
    >
      {props.children}
    </button>
  );
}
